import { formatMinor } from '@/kova/format'
import type {
  BalLeg, Block, Cell, ChipCell, Column, NoteBlock, Source, SubCell, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinor as sharedExactMinor, isCurrencyCode } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Ledger & Reconciliation › Balances vs provider (`lg-vs`), read from
 * `GET /v1/admin/ledger/balances` (go-wallet#386, handler
 * `internal/server/admin_ledger.go`).
 *
 * # This is the screen invariant 2 is about
 *
 * Our ledger is the *operational* system of record and the institution's records
 * are *legally authoritative*: they agree after reconciliation, and this screen
 * is the one place in the console that exists for the case where they do not.
 * Every consequence below follows from that single sentence.
 *
 * **Which side a figure came from is said per figure, not once for the screen.**
 * A response-level "ledger" badge would keep saying ledger over a column of
 * provider snapshots, and the operator quoting a number in a dispute with the
 * institution would be quoting the wrong one. So each figure's own
 * `provenance.authority` is rendered beside it, and a figure whose provenance
 * does not corroborate the side it is drawn under is named rather than shown
 * quietly under the wrong heading.
 *
 * **The difference arrives computed and is never recomputed here.** Subtracting
 * in the console would force this file to decide what a missing side means, and
 * the answer it would reach for is zero — which turns "we could not read the
 * institution" into "the institution shows nothing there", a discrepancy alarm
 * the size of the whole balance. `dashboard.Delta` refuses instead, once,
 * server-side, and carries the refusal as the figure's reason.
 *
 * **`reconciled` is the backend's judgement and is rendered, never derived.** It
 * is cross-checked against the difference on screen and the check is
 * one-directional: a disagreement can take "tied" away and can never grant it.
 * An unreadable position is not a reconciled one.
 *
 * # The response has two halves and only one of them can fail
 *
 * `by_currency` is the whole-book position — the figure most operators came for
 * — and the handler will not lose it to a paging error in the per-account half.
 * So `by_account` carries its OWN availability and reason, and is **absent
 * rather than empty** when it could not be read. An empty array and a missing
 * one are the same picture on screen and the second one reads as "no account
 * disagrees with the institution" during exactly the incident when nobody
 * verified that. Nothing here renders a table from an absent array.
 *
 * # What the per-account half is, precisely
 *
 * `LedgerReader.Positions` selects `class = 'customer_wallet' AND closed_at IS
 * NULL`, ordered by `account_id DESC`. Three things follow that the screen has
 * to say out loud, because none of them is visible in the rows:
 *
 *   - It is not the chart of accounts. `provider_settlement`, `pending_out`,
 *     fees and suspense are not in it, so an operator account out of step with
 *     the institution does not appear here at all.
 *   - It is not sorted by difference, which is what the design draws. The
 *     largest drift is wherever its account id puts it.
 *   - Which, combined with a truncated page, is the failure worth naming: the
 *     biggest discrepancy in the book can be on a page nobody looked at, and a
 *     screen that renders the first 200 rows without saying so reads as the
 *     whole book agreeing.
 *
 * # No control, and that is structural
 *
 * Nothing on this screen mutates anything. Law 1 puts account state beyond this
 * console entirely — only the institution sets it, and we display it. There is
 * no adjustment, no sweep and no "reconcile away" either: a finding here
 * resolves through an attributed compensating posting executed at the
 * institution first (invariant 2b), or it does not resolve. A button that
 * netted a difference locally would be unlicensed e-money issuance wearing a
 * convenience label.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_ledger.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

/** Where a figure's truth comes from. Invariant 2, made visible per figure. */
export type Authority = 'operational' | 'provider-confirmed' | 'analytics'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

/**
 * Minor units and an explicit ISO code, always together (invariants 3 and 4).
 *
 * `amount_minor` is typed `number | string` because the backend is mid-flip.
 * `moneyView.AmountMinor` is an `int64` serialised as a JSON number today, and
 * go-wallet#411 moves it to a decimal string for the reason go-wallet#377
 * already moved the customer surface: `JSON.parse` rounds past 2^53 before any
 * client code runs. The backend's own comment says it cannot flip until the
 * console reads both forms, so reading both is what makes that flip a
 * non-event.
 *
 * The string is never routed through `Number()` on the way in. That is the
 * whole point: `Number("9007199254740993")` is `9007199254740992`, which is a
 * safe integer, so the guard would pass for the one value it exists to catch.
 * See `exactMinor`.
 */
export interface MoneyView {
  readonly amount_minor: number | string
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  /** Absent, never zero, when the figure is not available. */
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

/** One currency's whole-book position, composed by the same function the Today board uses. */
export interface MoneyAtRestView {
  readonly currency: string
  readonly ledger: FigureView
  readonly provider: FigureView
  readonly delta: FigureView
  readonly excluded_by_open_cases: FigureView
  readonly reconciled: boolean
}

/**
 * The cap that was applied, travelling with the rows.
 *
 * `has_more` is answered by reading one row past the cap, never by counting the
 * table, and `next_cursor` is present only when there is more — a cursor on a
 * final page is a button that leads nowhere.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

/**
 * One account's line on the ours-versus-theirs comparison.
 *
 * `provider_as_of` is absent when the institution has never reported a balance
 * for this account — and it can also arrive as Go's zero instant, because the
 * snapshot row's `provider_as_of` is nullable while the snapshot itself is not.
 * Neither is a date, and both matter: a balance that matches the one the
 * institution reported last Tuesday is not agreement today.
 */
export interface LedgerPositionView {
  readonly account_id: number
  readonly class: string
  readonly scope: string
  readonly currency: string
  readonly ledger: FigureView
  readonly provider: FigureView
  readonly delta: FigureView
  readonly provider_as_of?: string
  readonly reconciled: boolean
}

export interface LedgerBalancesResponse {
  readonly generated_at: string
  readonly by_currency: readonly MoneyAtRestView[]
  /** On the COLLECTION, so "the query failed" and "no account holds a position" differ. */
  readonly by_account_availability: string
  readonly by_account_reason?: string
  readonly page: PageView
  /** Absent, not empty, when the read failed. `[]` whenever it succeeded. */
  readonly by_account?: readonly LedgerPositionView[]
}

export const LEDGER_BALANCES_PATH = '/ledger/balances'

const SOURCE = 'postgres · ledger_balances + provider_balance_snapshots (GET /v1/admin/ledger/balances)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `availabilityOf` takes on the dashboard: an unrecognised
 * value resolves to the reading that shows the operator LESS confidence, never
 * more. A fourth state added upstream and defaulted to "available" would put an
 * unverified number on the one screen whose whole job is saying whether two
 * records agree.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

/** The backend's own words, verbatim, or an honest sentence when it sent none. */
function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `the backend reported availability "${raw}", which this console does not know, so it is treated as unreadable`
  }
  return 'could not be read, and this is not a balance of zero'
}


/**
 * Minor units we can state exactly, from either wire form, or nothing.
 *
 * # This helper exists because `money.exactMinor` does not, yet
 *
 * #38 is the issue that widens the console's amount reader and adds
 * `exactMinor` to `src/lib/money/money.ts`; it has not merged, so this is the
 * local stand-in and it is deliberately the same shape. When #38 lands, this
 * function is deleted and its callers point at the shared one — the wire type
 * above is already the widened one, so nothing else has to move.
 *
 * # The order of the checks is the whole of it
 *
 * A string is validated and converted through `BigInt`, never through
 * `Number()`. `Number("9007199254740993")` is `9007199254740992` — a perfectly
 * safe integer — so a guard applied after the coercion passes for precisely the
 * value it was written to catch, and the screen prints a balance wrong in its
 * last digit while looking entirely plausible. On a reconciliation screen a
 * number that is quietly wrong is worse than one we admit we cannot state, so
 * anything out of exact range returns `null` and takes the unreadable path.
 *
 * Only `-?\d+` is accepted. An `int64` rendered as text is always that shape; a
 * `"5.0"` or a `"5e3"` is not minor units and is not repaired into some.
 */
/**
 * Delegates to `money.exactMinor`, and does not reimplement it.
 *
 * Four copies of this rule were written for #40, and they had already drifted
 * before the branch was a day old: one accepted a leading `+`, two trimmed
 * whitespace, one returned `bigint`. The rule has a trap in it — validating a
 * string by `Number()`-ing it first accepts `"9007199254740993"`, the one value
 * it exists to refuse — and a rule with a trap is a rule to have exactly once.
 *
 * The shared one is stricter than the copies here were: no surrounding
 * whitespace, no leading `+`. Neither is a shape `strconv.FormatInt` emits, so
 * refusing them narrows the guard rather than breaking a real payload.
 */
function exactMinor(raw: number | string | undefined): number | null {
  return sharedExactMinor(raw)
}

/**
 * A plain integer we can state exactly, or nothing.
 *
 * Used for account ids and page counters. An account id past 2^53 has already
 * lost its last digits inside `JSON.parse`, and an id wrong in its last digits
 * is the wrong account — on this screen, the wrong account being reported to
 * the institution as out of step.
 */
function exactInteger(value: unknown): number | null {
  if (typeof value !== 'number') return null
  return Number.isSafeInteger(value) ? value : null
}

/**
 * An instant off the wire, in the four states it can actually arrive in.
 *
 * The zero one is not defensive. `provider_balance_snapshots.provider_as_of` is
 * nullable while the snapshot row is not, so `ComposePositions` can hand back a
 * pointer to Go's zero `time.Time`, which serialises as `0001-01-01T00:00:00Z`
 * and parses perfectly. Rendered as a date it reads as a snapshot taken two
 * thousand years ago, and rendered through an age calculation it reads as
 * unimaginably stale — both are wrong in a way that looks like a finding. It is
 * a snapshot with no as-of, and it says so.
 */
type Instant =
  | { readonly kind: 'absent' }
  | { readonly kind: 'zero' }
  | { readonly kind: 'unparseable'; readonly raw: string }
  | { readonly kind: 'at'; readonly at: Date; readonly text: string }

function instantOf(iso: string | undefined): Instant {
  if (iso === undefined || iso === '') return { kind: 'absent' }
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return { kind: 'unparseable', raw: iso }
  if (at.getUTCFullYear() <= 1) return { kind: 'zero' }
  return { kind: 'at', at, text: readAtStamp(at) }
}

/**
 * An instant for the header, returned verbatim rather than invented when it
 * will not parse. An ugly stamp is recoverable; a confidently wrong one beside
 * a balance is not.
 */
function stamp(iso: string | undefined): string {
  const parsed = instantOf(iso)
  switch (parsed.kind) {
    case 'at': return parsed.text
    case 'unparseable': return parsed.raw
    default: return ''
  }
}

/** Compact elapsed time. Both ends come off the payload — no ambient clock here. */
function compactSpan(seconds: number): string {
  const s = Math.max(0, Math.trunc(seconds))
  if (s < 60) return `${String(s)}s`
  if (s < 3600) return `${String(Math.floor(s / 60))}m`
  if (s < 86400) return `${String(Math.floor(s / 3600))}h`
  return `${String(Math.floor(s / 86400))}d`
}

function provenanceMeta(p: ProvenanceView | undefined): string {
  if (p === undefined) return ''
  const at = stamp(p.read_at)
  return at === '' ? p.source : `${p.source} · read ${at}`
}

// ---------------------------------------------------------------------------
// Which side a figure belongs to
// ---------------------------------------------------------------------------

/**
 * The four roles a figure plays on this screen.
 *
 * `difference` is separated from the two sides because it is neither of them:
 * `dashboard.Delta` attributes it to `provider-confirmed`, since the comparison
 * is only meaningful against the record that wins the discrepancy.
 */
type Side = 'ours' | 'theirs' | 'difference' | 'excluded'

/**
 * What each side's provenance is expected to say, and `null` where nothing is.
 *
 * Checked rather than assumed. The heading over a column is a claim about which
 * record answered, and a figure whose own provenance says otherwise is the one
 * case where the operator would read the institution's number as ours — the
 * precise failure invariant 2 exists to prevent. `excluded` expects nothing
 * because `NotBuiltFigure` carries no provenance at all, by construction.
 */
const SIDE_AUTHORITY: Readonly<Record<Side, Authority | null>> = {
  ours: 'operational',
  theirs: 'provider-confirmed',
  difference: 'provider-confirmed',
  excluded: null,
}

/** How each side is described to an operator, in the language of invariant 2. */
const SIDE_STANDING: Readonly<Record<Side, string>> = {
  ours: 'our operational record',
  theirs: 'the institution’s record, which is legally authoritative',
  difference: 'the difference the backend computed between the two',
  excluded: 'an amount held out of available',
}

/** Used only when the backend sent an empty label; a nameless leg reads as nothing. */
const SIDE_FALLBACK_LABEL: Readonly<Record<Side, string>> = {
  ours: 'Ours (operational)',
  theirs: 'The institution’s record (legally authoritative)',
  difference: 'Difference (ours − theirs)',
  excluded: 'Excluded by open cases',
}

function labelOf(f: FigureView, side: Side): string {
  return f.label === '' ? SIDE_FALLBACK_LABEL[side] : f.label
}

/** A figure that can carry a number, or the reason it cannot. */
type Placement =
  | { readonly ok: true; readonly minor: number; readonly currency: string }
  | { readonly ok: false; readonly reason: string }

/**
 * Whether one figure may be shown as an amount, and if not, why not.
 *
 * ONE decision, asked by the balance block, by the table and by the screen's own
 * provenance chip, so the three cannot disagree about what the operator was
 * told. Three of the refusals fire while the backend says `available` — a law-12
 * analytics authority, a value it did not send, an amount whose currency cannot
 * be named — and each of them takes a side off a reconciliation. A leg quietly
 * absent under a `Live` badge is the same lie as a fabricated zero, told by
 * omission instead.
 */
function placeFigure(f: FigureView): Placement {
  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return { ok: false, reason: reasonFor(availability, f.availability, f.reason) }
  }

  if (f.value === undefined) {
    return {
      ok: false,
      reason: 'the backend reported this figure as available and sent no amount, which is not a balance of zero',
    }
  }

  // Law 12. There is no component on this console that may render an
  // analytics-sourced balance: the mirror is eventually consistent and never a
  // source of truth, and there is no freshness stamp that would make comparing
  // one against a ledger figure mean anything. Withheld, not badged.
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason: 'refused — reported as analytics-sourced, and no balance on this console may come from the analytics mirror (law 12)',
    }
  }

  const minor = exactMinor(f.value.amount_minor)
  if (minor === null) {
    return {
      ok: false,
      reason: 'the amount did not arrive as minor units this console can state exactly, so it is not shown rather than shown wrong in its last digits',
    }
  }

  // The full predicate, not `currency !== ''`. `formatMinor` throws on anything
  // that is not three letters — correctly, since an amount whose currency we
  // cannot name is not a value this system may carry (invariant 4) — but it is
  // called during render, so a `"EU"` off the wire would take the whole
  // reconciliation screen to the error boundary instead of producing one named
  // absence.
  if (!isCurrencyCode(f.value.currency)) {
    return {
      ok: false,
      reason: f.value.currency === ''
        ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4)'
        : `the amount arrived with ${JSON.stringify(f.value.currency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4)`,
    }
  }

  // Normalised rather than passed through: the ISO span beside an amount renders
  // this string verbatim, and an operator comparing our leg against the
  // institution's should not have to decide whether `eur` and `EUR` are the
  // same currency.
  return { ok: true, minor, currency: f.value.currency.trim().toUpperCase() }
}

/**
 * A figure whose provenance does not corroborate the side it is drawn under.
 *
 * Two sentences rather than one because they are read in two places and the
 * places want different lengths: `note` is the prose beside the block, `missing`
 * is one line in the provenance chip's list of what the operator was not told.
 */
interface Attribution {
  readonly note: string
  readonly missing: string
}

/**
 * Whether a shown figure's own provenance corroborates the side it is shown
 * under, and what to say when it does not.
 *
 * Asked only of figures that were placed — an absence is already accounted for
 * as an absence. This is the other half: a number that IS on screen, under a
 * heading that says which record it came from, and whose provenance says a
 * different one. The number is still rendered, because it was read and
 * suppressing it would trade one absence for another; what is withheld is the
 * claim about whose record it is.
 */
function attributionProblem(f: FigureView, side: Side, who: string): Attribution | null {
  const expected = SIDE_AUTHORITY[side]
  if (expected === null) return null

  const label = labelOf(f, side)
  const missing = `which record “${label}” on ${who} belongs to`

  if (f.provenance === undefined) {
    return {
      missing,
      note: `${who}: “${label}” is drawn as ${SIDE_STANDING[side]} and carries no provenance at all, so which record it belongs to is not established (invariant 2)`,
    }
  }
  if (f.provenance.authority !== expected) {
    return {
      missing,
      note: `${who}: “${label}” is drawn as ${SIDE_STANDING[side]} and its provenance claims authority "${f.provenance.authority}" rather than "${expected}", so which record it belongs to is not established (invariant 2)`,
    }
  }
  return null
}

/**
 * The band's source badge, derived from the figures rather than asserted.
 *
 * A hardcoded badge would keep saying "ledger" on the day something
 * analytics-sourced appears in the band, which is precisely the case law 12
 * exists to make visible.
 */
function bandSource(figures: readonly FigureView[]): { src?: Source; fresh?: string } {
  const authorities = new Set<string>()
  let latest = ''
  for (const f of figures) {
    if (f.provenance === undefined) continue
    authorities.add(f.provenance.authority)
    if (f.provenance.read_at > latest) latest = f.provenance.read_at
  }
  if (authorities.has('analytics')) return { src: 'analytics', fresh: `fresh ${stamp(latest)}` }
  if (authorities.size === 1 && authorities.has('provider-confirmed')) return { src: 'provider' }
  if (authorities.size === 0) return {}
  return { src: 'ledger' }
}

// ---------------------------------------------------------------------------
// The per-currency band
// ---------------------------------------------------------------------------

interface Slot {
  readonly side: Side
  readonly figure: FigureView
}

/** One currency's figures, in the order an operator reads them. */
function slotsOf(m: MoneyAtRestView): readonly Slot[] {
  return [
    { side: 'ours', figure: m.ledger },
    { side: 'theirs', figure: m.provider },
    { side: 'difference', figure: m.delta },
    { side: 'excluded', figure: m.excluded_by_open_cases },
  ]
}

interface Findings {
  /** Figures that could not be shown, each with the reason it could not. */
  readonly withheld: readonly string[]
  /** Figures that were shown, whose provenance did not corroborate their side. */
  readonly attribution: readonly Attribution[]
}

interface RenderedCurrency extends Findings {
  readonly legs: readonly BalLeg[]
}

/**
 * Turns one currency's figures into balance legs.
 *
 * A leg structurally requires a number, so a figure without one cannot be a leg.
 * It is not dropped: it comes back in `withheld` and is rendered beside the
 * block as prose. Silently shortening the block would leave an operator
 * comparing two sides of a reconciliation without being told a third figure
 * exists and is missing.
 *
 * Tone is the verdict, not the provenance — the tag carries which record
 * answered. Neither side of a comparison is "good" on its own; only their
 * agreement is, which is why only the difference leg is ever green.
 */
function renderCurrency(m: MoneyAtRestView): RenderedCurrency {
  const legs: BalLeg[] = []
  const withheld: string[] = []
  const attribution: Attribution[] = []
  const who = m.currency === '' ? 'a position with no currency' : m.currency

  for (const { side, figure } of slotsOf(m)) {
    const label = labelOf(figure, side)
    const placed = placeFigure(figure)
    if (!placed.ok) {
      withheld.push(`${who} — ${label}: ${placed.reason}`)
      continue
    }

    const problem = attributionProblem(figure, side, who)
    if (problem !== null) attribution.push(problem)

    const tone: Tone = side === 'difference'
      ? (placed.minor === 0 ? 'g' : 'r')
      : side === 'excluded' ? 'a' : 'n'
    const tag = figure.provenance?.authority ?? 'unattributed'
    const sub = provenanceMeta(figure.provenance)

    legs.push(side === 'excluded'
      ? [label, placed.minor, placed.currency, sub, tag, tone, tone, 'excl']
      : [label, placed.minor, placed.currency, sub, tag, tone, tone])
  }

  return { legs, withheld, attribution }
}

/**
 * The verdict line under the arithmetic.
 *
 * Read from the difference ON SCREEN as well as from `reconciled`, and the check
 * only ever runs one way: a disagreement between the two takes "tied" away and
 * can never grant it. `reconciled` is not recomputed — it is the backend's
 * judgement and this line states it — but a judgement contradicted by the number
 * beside it is not a judgement an operator should be handed unqualified.
 */
function bindingLine(m: MoneyAtRestView, at: string): string {
  const placed = placeFigure(m.delta)
  const asOf = at === '' ? 'an unstated time' : at

  if (!placed.ok) {
    return `Not tied — the difference is unknown, not zero. As of ${asOf}.`
  }
  const shown = `${formatMinor(placed.minor, placed.currency)} ${placed.currency}`

  if (placed.minor === 0 && m.reconciled) return `Tied as of ${asOf}.`
  if (placed.minor === 0) {
    return `Not tied — the difference reads zero and the backend did not call this position reconciled. The two are renderings of one judgement and they disagree, so agreement is not stated. As of ${asOf}.`
  }
  if (m.reconciled) {
    return `Not stated — the backend called this position reconciled and sent a difference of ${shown}, which cannot both be true. As of ${asOf}.`
  }
  return `Not tied — ours minus the institution’s is ${shown}, as of ${asOf}.`
}

interface CurrencyBand extends Findings {
  readonly blocks: readonly Block[]
}

function currencyBand(m: MoneyAtRestView, at: string): CurrencyBand {
  const { legs, withheld, attribution } = renderCurrency(m)
  const { src, fresh } = bandSource(slotsOf(m).map((s) => s.figure))

  const bal: Block = {
    t: 'bal',
    title: `Ours versus the institution — ${m.currency}`,
    ...(src === undefined ? {} : { src }),
    ...(fresh === undefined ? {} : { fresh }),
    note: 'One block per currency, never blended. A euro surplus cancelling a dollar shortfall into apparent agreement is exactly what the split prevents.',
    legs,
    formula: 'ours − the institution’s = difference · computed by the backend, never here',
    binding: bindingLine(m, at),
  }

  const blocks: Block[] = [bal]
  if (withheld.length > 0) {
    // Beside the block, not inside it, and never instead of it. The reason
    // travels with the label because "—" tells an operator nothing they can act
    // on, and the difference between "#103 has not been built" and "the read
    // failed" decides whether they page anyone.
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not shown',
      title: `${m.currency} — ${String(withheld.length)} figure(s) could not be placed on the balance`,
      body: withheld.join(' · '),
      ref: 'A missing figure is not a figure of zero.',
    })
  }
  return { blocks, withheld, attribution }
}

// ---------------------------------------------------------------------------
// The per-account band
// ---------------------------------------------------------------------------

const ACCOUNT_COLUMNS: readonly Column[] = [
  ['Account', 'm', '96px'],
  ['Class', 'x', 'minmax(150px,1.1fr)'],
  ['Scope', 'x', 'minmax(120px,1fr)'],
  ['Cur', 'm', '72px'],
  ['Ours · operational', 'a', 'minmax(140px,1fr)'],
  ['Institution · authoritative', 'a', 'minmax(150px,1.1fr)'],
  ['Difference · ours − theirs', 'a', 'minmax(155px,1.1fr)'],
  ['Institution as of (UTC)', 's', 'minmax(210px,1.4fr)'],
  ['Tied', 'c', '132px'],
]

/**
 * What an amount column holds when the figure could not be placed.
 *
 * A word, not a dash, and never a zero. The `Table` renderer treats a bare
 * string in an amount column as a placeholder and prints it as text, so this is
 * the shape available — and "not read" at least says which of the two things it
 * is. The reason itself is too long for a 140px cell and is carried, verbatim
 * and named against this account, in the withheld note under the table.
 */
const UNPLACED_AMOUNT = 'not read'

interface Chipped {
  readonly cell: ChipCell
  readonly problem: string | null
}

/**
 * Whether this account agrees with the institution, as the backend judged it.
 *
 * Five outcomes, because "we could not read one side" and "the two sides differ"
 * are different findings with different next steps, and neither is "tied". The
 * console does not derive agreement — `dashboard.Delta` sets `Reconciled` only
 * when both sides are available and equal — it renders that judgement and
 * refuses to upgrade it when the difference on screen disagrees.
 */
function tiedChip(reconciled: unknown, delta: Placement, who: string): Chipped {
  if (typeof reconciled !== 'boolean') {
    return {
      cell: ['unclear', 'r'],
      problem: `${who}: the row did not say whether the backend reconciled it, and agreement is the backend's judgement rather than one this console derives`,
    }
  }
  if (!delta.ok) {
    return reconciled
      ? {
          cell: ['contradictory', 'r'],
          problem: `${who}: the backend called this reconciled and sent no difference this console could read, so agreement is not stated`,
        }
      : {
          cell: ['unknown', 'r'],
          problem: `${who}: the difference could not be read, so whether this account agrees with the institution is unknown — an unreadable position is not a reconciled one`,
        }
  }
  if (delta.minor === 0) {
    return reconciled
      ? { cell: ['tied', 'g'], problem: null }
      : {
          cell: ['not stated', 'a'],
          problem: `${who}: the difference reads zero and the backend did not call this reconciled; the two are renderings of one judgement and they disagree, so agreement is not stated`,
        }
  }
  return reconciled
    ? {
        cell: ['contradictory', 'r'],
        problem: `${who}: the backend called this reconciled and the difference it sent is not zero, so agreement is not stated`,
      }
    : { cell: ['not tied', 'r'], problem: null }
}

/**
 * When the institution last reported on this account, and how current that is.
 *
 * The age is stated and NOT graded. The design draws a green/amber chip here,
 * and a threshold for "how stale is too stale" is a policy — the backend states
 * no maximum age for a provider snapshot, so any number this console picked
 * would be one it made up and then rendered as though the system held it. The
 * elapsed span is arithmetic on two timestamps the same response carried, which
 * is a fact; where the line falls is not, and the not-shown note says so.
 */
function asOfCell(
  raw: string | undefined, providerPlaced: boolean, readAt: Date | null,
  providerReason: string,
): SubCell {
  const parsed = instantOf(raw)

  if (parsed.kind === 'absent') {
    if (providerPlaced) {
      return ['not stated', 'the institution reported a balance and no as-of came with it, so how current it is cannot be judged']
    }
    // "Never reported" is a CAUSE, and this cell cannot see it.
    //
    // No as-of plus no placeable figure has more than one explanation, and the
    // backend distinguishes them on the figure's own reason: the institution
    // has never reported on this account; the snapshot read failed; the
    // snapshot arrived in a currency the account was not opened in. Asserting
    // the first flatly makes the other two read as "the institution shows
    // nothing here", which on this screen is the difference between an account
    // opened this morning and money that has gone missing.
    //
    // So the cause is quoted from the figure that knows it, and invented only
    // when there is nothing to quote.
    return providerReason === ''
      ? ['none', 'no figure came from the institution for this account and the response gave no reason, so why is unknown — this is not a balance of zero']
      : ['none', providerReason]
  }
  if (parsed.kind === 'zero') {
    return ['not stated', 'the snapshot carries no as-of date, so how current it is cannot be judged']
  }
  if (parsed.kind === 'unparseable') {
    return [parsed.raw, 'this did not parse as an instant, so how current the snapshot is cannot be judged']
  }
  if (readAt === null) {
    return [parsed.text, 'the response did not state when it was generated, so the snapshot’s age cannot be given']
  }

  const ms = readAt.getTime() - parsed.at.getTime()
  return ms < 0
    ? [parsed.text, 'dated AFTER this read — the two clocks disagree, so its age cannot be stated']
    : [parsed.text, `${compactSpan(ms / 1000)} before this read`]
}

interface RowRead extends Findings {
  readonly cells: readonly Cell[]
}

const UNREADABLE_ROW: readonly Cell[] = [
  '—', '—', '—', '—',
  UNPLACED_AMOUNT, UNPLACED_AMOUNT, UNPLACED_AMOUNT,
  ['—', 'this row did not arrive as an account position'],
  ['unreadable', 'r'],
]

/**
 * A figure the row did not carry at all.
 *
 * Read as unreadable rather than as absent, because the difference between the
 * two is the difference between "the institution has nothing for this account"
 * and "this payload is missing a field". Both are worth knowing and only the
 * first is a fact about the money.
 */
const MISSING_FIGURE: FigureView = {
  label: '',
  availability: 'unavailable',
  reason: 'the row did not carry this figure at all, so this side of the comparison cannot be stated from it',
}

const NOT_PLACED: Placement = { ok: false, reason: 'the row did not carry this figure at all' }

function textOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback
}

function amountCell(placed: Placement | undefined): Cell {
  if (placed === undefined || !placed.ok) return UNPLACED_AMOUNT
  return [placed.minor, placed.currency]
}

/**
 * One account's position, as a row.
 *
 * Takes `unknown` because `Array.isArray` says nothing about the elements. A row
 * that is not an object is still rendered — as an unreadable row — because
 * dropping it shortens a list an operator is reading to decide whether the book
 * agrees with the institution, and tells them nothing about why it got shorter.
 */
function readAccountRow(raw: unknown, readAt: Date | null): RowRead {
  if (typeof raw !== 'object' || raw === null) {
    return {
      cells: UNREADABLE_ROW,
      withheld: ['an account position that did not arrive as an object, so none of its figures are shown'],
      attribution: [],
    }
  }

  const row = raw as Partial<LedgerPositionView>
  const withheld: string[] = []
  const attribution: Attribution[] = []

  const id = exactInteger(row.account_id)
  const who = id === null ? 'an unidentified account row' : `account ${String(id)}`
  if (id === null) {
    withheld.push(
      `${who}: the account id could not be stated exactly, so it is not shown — an id wrong in its last digits names a different account, and this is the screen its figures would be reported from`,
    )
  }

  const sides: readonly Slot[] = [
    { side: 'ours', figure: row.ledger ?? MISSING_FIGURE },
    { side: 'theirs', figure: row.provider ?? MISSING_FIGURE },
    { side: 'difference', figure: row.delta ?? MISSING_FIGURE },
  ]

  const placements = sides.map(({ side, figure }) => {
    const placed = placeFigure(figure)
    if (!placed.ok) {
      withheld.push(`${who} — ${labelOf(figure, side)}: ${placed.reason}`)
    } else {
      const problem = attributionProblem(figure, side, who)
      if (problem !== null) attribution.push(problem)
    }
    return placed
  })

  const [ours, theirs, difference] = placements
  const tied = tiedChip(row.reconciled, difference ?? NOT_PLACED, who)
  if (tied.problem !== null) withheld.push(tied.problem)

  return {
    cells: [
      id === null ? '—' : String(id),
      textOr(row.class, 'not stated'),
      textOr(row.scope, 'not stated'),
      textOr(row.currency, '—'),
      amountCell(ours),
      amountCell(theirs),
      amountCell(difference),
      asOfCell(row.provider_as_of, theirs?.ok === true, readAt, row.provider?.reason ?? ''),
      tied.cell,
    ],
    withheld,
    attribution,
  }
}

// ---------------------------------------------------------------------------
// Pagination, stated rather than implied
// ---------------------------------------------------------------------------

interface Paging {
  readonly footer: string
  /** Present when the list is, or may be, short of the whole set. */
  readonly truncation: string | null
}

/**
 * What the page descriptor says, and what it does not.
 *
 * A capped list rendered with no statement that it was capped reads as the whole
 * book. That is bad on any list and worse on this one: `Positions` orders by
 * `account_id DESC` and not by difference, so the largest discrepancy is
 * wherever its account id puts it — quite possibly on a page nobody looked at,
 * under a screen that showed no disagreement.
 *
 * `has_more` that did not arrive as a boolean is treated as "not known to be
 * complete", never as complete. That is the direction that costs an operator a
 * second look rather than a missed drift.
 */
function pagingOf(page: Partial<PageView> | undefined, shown: number): Paging {
  const limit = exactInteger(page?.limit)
  const returned = exactInteger(page?.returned)
  const hasMore = typeof page?.has_more === 'boolean' ? page.has_more : null
  const cursor = exactInteger(page?.next_cursor)

  const parts: string[] = [`${String(shown)} account(s) on this page`]
  if (limit === null) {
    parts.push('the server echoed no page limit')
  } else {
    parts.push(`the server's limit was ${String(limit)}`)
  }
  if (returned !== null && returned !== shown) {
    parts.push(
      `the descriptor says ${String(returned)} row(s) were returned and ${String(shown)} arrived, so the two disagree and this page is not known to be what the server sent`,
    )
  }

  if (hasMore === null) {
    parts.push('and it did not say whether more remain')
    return {
      footer: parts.join(' · '),
      truncation: 'The server did not say whether accounts remain past this page, so this list is not known to be the whole set.',
    }
  }
  if (!hasMore) {
    parts.push('and it says no accounts remain past this page')
    return { footer: parts.join(' · '), truncation: null }
  }

  parts.push('and it says MORE ACCOUNTS REMAIN — this list is truncated')
  if (cursor !== null) parts.push(`next cursor ${String(cursor)}`)
  else if (page?.next_cursor !== undefined) {
    parts.push('the next cursor could not be stated exactly, so paging past here is not safe from this screen')
  }

  return {
    footer: parts.join(' · '),
    truncation:
      'The server says accounts remain past this page and they are not on this screen. This read is ordered by account id descending and NOT by difference — the design sorts by difference and this endpoint does not — so the largest discrepancy in the book may be on a page nobody has looked at. A short list here is not a book that agrees.',
  }
}

// ---------------------------------------------------------------------------
// The account band, assembled
// ---------------------------------------------------------------------------

interface AccountBand extends Findings {
  readonly blocks: readonly Block[]
  /** Lines for the provenance chip that are not row-level problems. */
  readonly missing: readonly string[]
}

const ACCOUNT_NOTE =
  'Open customer wallet accounts only, ordered by account id descending. This is not the chart of accounts: provider_settlement, pending_out, fees and suspense are not selected by this read, and neither is a closed account.'

function accountTable(rows: readonly (readonly Cell[])[], footer: string): TableBlock {
  return {
    t: 'table',
    title: 'ledger_balances vs provider_balance_snapshots',
    // The console's source vocabulary has no 'operational' member and 'ledger'
    // is the word it already uses for our own Postgres. Both sides of the
    // comparison are read from it; what the badge is for is saying this is not
    // the analytics mirror (law 12), and the per-row attribution says which
    // record each figure belongs to.
    src: 'ledger',
    note: ACCOUNT_NOTE,
    savedView: 'Account id descending',
    // No facet bar. This read sends no filters at all, and a chip reading
    // `currency EUR` would claim one that is not applied — which on a screen
    // whose job is completeness is the worst possible thing to imply.
    facets: false,
    cols: ACCOUNT_COLUMNS,
    rows,
    footer,
    foot2: 'read-only — no freeze, no adjustment, no sweep: a difference here resolves through an attributed compensating posting or not at all',
  }
}

/**
 * The per-account half, or the honest account of why there is not one.
 *
 * Every branch produces something visible. The one thing that may never happen
 * is an empty table drawn from an absent array: "no account disagrees with the
 * institution" is a sentence an operator stops investigating on, and the read
 * that failed never said it.
 */
function accountBand(t: LedgerBalancesResponse, readAt: Date | null): AccountBand {
  const availability = availabilityOf(t.by_account_availability)
  const reason = reasonFor(availability, t.by_account_availability, t.by_account_reason)
  const rows: readonly unknown[] | null = Array.isArray(t.by_account) ? t.by_account : null

  if (availability !== 'available') {
    // The array is ignored even if one arrived. The collection-level
    // availability is the field the handler sets deliberately when the read
    // failed, and trusting rows the backend has disowned is the fail-open
    // direction on the one band whose emptiness is a claim about the money.
    const drift = rows === null
      ? ''
      : ' A by_account array arrived alongside that availability and is not rendered: the backend marks the collection unreadable when the read failed, so rows under that flag are not rows it stands behind.'

    return {
      blocks: [{
        t: 'note',
        tone: availability === 'not_built' ? 'a' : 'r',
        kicker: 'Per-account comparison',
        title: availability === 'not_built'
          ? 'The per-account comparison is not built'
          : 'The per-account comparison could not be read',
        body: `${reason}${drift} No account-level table is shown, because an empty one would read as “no account disagrees with the institution” — and that is precisely what this read did not establish. The whole-book position above is unaffected: the handler degrades this band rather than the response, so the figure most operators came for survives a paging failure here.`,
        ref: 'by_account is absent, not empty, when the read failed',
      }],
      withheld: [],
      attribution: [],
      missing: [`every per-account position (${availability === 'not_built' ? 'not built' : 'could not be read'})`],
    }
  }

  // `by_account` is tagged `omitempty` on the Go side (go-wallet#421), and
  // `encoding/json` omits a slice at length zero regardless of nilness. So a
  // per-account read that SUCCEEDED and matched no accounts sends exactly the
  // same bytes as one whose array went missing: availability `available`, no
  // key. The response carries `by_account_availability` precisely so those two
  // can be told apart, and the encoder throws the distinction away again.
  //
  // This used to render red — "the two halves of the response disagree". That
  // was the right reading of the contract as written and the wrong thing on
  // screen: an environment with no customer wallet positions yet, which is
  // every fresh deployment and staging most of the time, would fly that alarm
  // permanently. An alarm that fires on the ordinary state is one operators
  // learn to scroll past, and this section cannot afford that any more than it
  // can afford false reassurance.
  //
  // So it is treated as the empty result it almost always is, and the console
  // says out loud that it cannot prove which of the two it got. Named in
  // `missing`, so the screen badges partial rather than live: this is not a
  // read that answered for everything.
  const ambiguousEmpty = rows === null
  const rowsRead = rows ?? []

  const reads = rowsRead.map((row) => readAccountRow(row, readAt))
  const paging = pagingOf(t.page, reads.length)
  // No table at all when the array was merely absent. An empty table is the
  // picture of "every account ties", and that is the one reading this screen
  // must never produce by accident — the note below says what is and is not
  // known instead. When the array really did arrive empty, the table is drawn:
  // zero rows under a header the operator asked for is a different statement
  // from no table, and it is the true one.
  const blocks: Block[] = ambiguousEmpty
    ? []
    : [accountTable(reads.map((r) => r.cells), paging.footer)]

  if (reads.length === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Nothing on this page',
      title: ambiguousEmpty
        ? 'No per-account positions came back, and the wire cannot say whether that means none exist'
        : 'The per-account read succeeded and returned no positions',
      body: ambiguousEmpty
        ? 'The response called the per-account read available and carried no accounts array. Those are the same bytes as a read that ran and matched nothing, because the field is omitted when empty (go-wallet#421) — so this console cannot tell the two apart and will not pick one. What it will not do is draw an empty table and let it read as “every account ties”, which nothing in this response says.'
        : 'This one is a real answer rather than a failure: the read ran and found no open customer wallet account to compare. It is still not a statement that the book agrees with the institution — it is a statement that there was nothing here to compare, which for a wallet with accepted customers is itself worth asking about.',
      ref: ambiguousEmpty
        ? 'by_account_availability = available, key absent — indistinguishable from empty'
        : 'by_account_availability = available, zero rows',
    })
  }

  if (paging.truncation !== null) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Truncated',
      title: 'This is not the whole list of accounts',
      body: paging.truncation,
      ref: 'page.has_more · ordered by account_id DESC, not by difference',
    })
  }

  return {
    blocks,
    withheld: reads.flatMap((r) => r.withheld),
    attribution: reads.flatMap((r) => r.attribution),
    // Named so the screen badges partial rather than live. A read that could
    // not establish whether it saw everything has not answered for everything.
    missing: [
      ...(paging.truncation === null ? [] : ['the accounts past this page (the server says more remain)']),
      ...(ambiguousEmpty
        ? ['whether any per-account position exists (the array was omitted, which is exactly what an empty one looks like)']
        : []),
    ],
  }
}

// ---------------------------------------------------------------------------
// Standing notes
// ---------------------------------------------------------------------------

/**
 * Invariant 2, on the screen rather than only in a document.
 *
 * Worth the vertical space here specifically. Everywhere else in the console the
 * two records agree and the distinction is theoretical; this is the screen
 * somebody opens when they do not, and it is the screen whose numbers end up
 * quoted in an email to the institution.
 */
const AUTHORITY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Invariant 2',
  title: 'Ours is the operational record; theirs wins',
  body: 'We run our own double-entry ledger and it is what every read, limit and balance check in this system runs on. It is not the legally authoritative record: the institution’s is, and a discrepancy resolves in its favour. Which side each figure on this screen came from is carried on the figure itself, from its own provenance, rather than asserted once for the page — a heading that claimed it for everything would keep claiming it on the day one figure came from somewhere else.',
  ref: 'operational · provider-confirmed · resolved by new postings, never by editing ours',
}

/**
 * There is no repair path, and the absence is the design rather than a gap.
 *
 * Said out loud because an operator looking at a difference will look for the
 * button, and not finding one should read as "there is deliberately none" rather
 * than as "this screen is half-built".
 */
const NO_CONTROL_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Read-only, structurally',
  title: 'Nothing on this screen can move, net or clear a difference',
  body: 'There is no freeze — only the institution freezes, and we display that state rather than setting it. There is no adjustment, no sweep, no bulk-clear and no “reconcile away”. Every value movement must be executed at the institution first and posted from the resulting provider event, so a difference here is closed by an attributed compensating posting with a reason code, composed where postings are composed. A control that netted it locally would be the operator issuing e-money, which is not something this system is licensed to do.',
  ref: 'invariant 2b · FR-5.5 · law 2 · the journal is append-only',
}

function notShownNote(): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Not shown',
    title: 'What the design draws here that this read does not answer for',
    body: 'The discrepancy case covering each difference — there is no case table yet (go-wallet#102–#105), so a row cannot say whether anyone owns its drift. A staleness verdict on the institution’s snapshot: the age is stated from the two timestamps in this response, and it is not graded, because the backend states no maximum age for a snapshot and a threshold invented here would be a policy this console made up and then rendered as though the system held it. Sorting by difference descending, which the design draws and this endpoint does not offer. And every account that is not an open customer wallet — the operator accounts are outside this read entirely.',
    ref: 'GET /v1/admin/ledger/balances is the whole of this screen',
  }
}

function attributionNote(found: readonly Attribution[]): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Attribution',
    title: `${String(found.length)} figure(s) are shown without an established record`,
    body: `${found.map((a) => a.note).join(' · ')}. Each of these numbers was read and is on screen; what is not established is whose record it is. On this screen that is the question — an operator reading the institution’s figure as ours, or ours as the institution’s, reaches the opposite conclusion about who owes whom.`,
    ref: 'invariant 2 · the authority is read from each figure’s own provenance',
  }
}

function withheldNote(lines: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Partly unreadable',
    title: `${String(lines.length)} figure(s) on this screen could not be stated`,
    body: lines.join(' · '),
    ref: 'A figure that could not be read is not a balance of zero.',
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * What is missing regardless of how well the read went.
 *
 * These are properties of the endpoint rather than of a response, so they are
 * listed on every composition and they are why this screen is permanently
 * `partial` and never `live`. `live` claims everything on the screen was read;
 * the case linkage was never built, the operator accounts are outside this
 * query, and `excluded_by_open_cases` comes back `not_built` on every currency
 * because the discrepancy case table is go-wallet#103. Badging that green once
 * the rows parse cleanly would say the picture is complete when it is complete
 * about open customer wallets and silent about the rest of the book.
 */
const STRUCTURAL_GAPS: readonly string[] = [
  'the discrepancy case covering each difference (no case table exists — go-wallet#102–#105)',
  'accounts that are not open customer wallets (this read selects class customer_wallet with closed_at null, so an operator account out of step with the institution does not appear)',
  'how stale the institution’s snapshot may be before it stops being agreement (the backend states no maximum age, and a threshold invented here would be a policy this console made up)',
]

function isBalancesResponse(payload: unknown): payload is LedgerBalancesResponse {
  if (typeof payload !== 'object' || payload === null) return false
  const r = payload as Partial<LedgerBalancesResponse>
  if (!Array.isArray(r.by_currency)) return false
  // Absent is the documented shape for a failed per-account read; present and
  // not an array is not a shape this console knows.
  return r.by_account === undefined || Array.isArray(r.by_account)
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every availability path — including the
 * two that matter most, an absent `by_account` and a truncated page — is
 * table-testable without a client (CLAUDE.md Part III).
 *
 * No clock is read. The age of the institution's snapshot is the span between
 * two timestamps this same response carried, which is why it can be stated at
 * all without one.
 */
export function composeLedgerBalances(t: LedgerBalancesResponse): ScreenData {
  const generated = instantOf(t.generated_at)
  const at = stamp(t.generated_at)
  const readAt = generated.kind === 'at' ? generated.at : null

  const blocks: Block[] = []
  const withheld: string[] = []
  const attribution: Attribution[] = []

  for (const m of t.by_currency) {
    const band = currencyBand(m, at)
    blocks.push(...band.blocks)
    withheld.push(...band.withheld)
    attribution.push(...band.attribution)
  }

  if (t.by_currency.length === 0) {
    // An absent band is not a reconciled one. Without this the screen would open
    // with the account table and read as though every currency tied.
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Whole-book position',
      title: 'No currency position was returned',
      body: 'Neither our ledger nor the institution’s record answered with a position in any currency, so nothing is reconciled here — including nothing being wrong. The per-currency band is composed from both sides at once and a currency present on one side alone still appears in it, so an empty band means neither side answered rather than that the book is empty.',
      ref: 'invariant 2 · by_currency came back with no entries',
    })
  }

  const accounts = accountBand(t, readAt)
  blocks.push(...accounts.blocks)
  withheld.push(...accounts.withheld)
  attribution.push(...accounts.attribution)

  blocks.push(AUTHORITY_NOTE, NO_CONTROL_NOTE, notShownNote())
  if (attribution.length > 0) blocks.push(attributionNote(attribution))
  if (withheld.length > 0) blocks.push(withheldNote(withheld))

  // Derived from the figures rather than asserted, for the reason the block
  // badges are: a hardcoded "operational" would keep saying operational on the
  // day a provider-confirmed figure is the only one that answered.
  const authorities = new Set<string>()
  for (const m of t.by_currency) {
    for (const { figure } of slotsOf(m)) {
      if (figure.provenance !== undefined) authorities.add(figure.provenance.authority)
    }
  }
  const accountRows: readonly unknown[] = Array.isArray(t.by_account) ? t.by_account : []
  for (const raw of accountRows) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Partial<LedgerPositionView>
    for (const f of [row.ledger, row.provider, row.delta]) {
      if (f?.provenance !== undefined) authorities.add(f.provenance.authority)
    }
  }

  const missing = [
    ...withheld,
    ...attribution.map((a) => a.missing),
    ...accounts.missing,
    ...STRUCTURAL_GAPS,
  ]

  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: authorities.size === 0
      ? 'unattributed'
      : [...authorities].sort((a, b) => a.localeCompare(b)).join(' + '),
    readAt: at,
    missing,
  }

  return { blocks, provenance }
}

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. Not an empty comparison — an empty comparison reads
 * as a book in agreement, which is the single most dangerous sentence this
 * screen can say — and never the design corpus, whose five invented rows carry
 * two invented open discrepancy cases.
 *
 * The provenance is `unread`, not `partial`: nothing here came from anywhere,
 * and the chip renders one word. `Partial` says some of this is real.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Balances vs provider',
      title: 'This screen could not be read',
      body: `${reason} No balance of ours, no figure of the institution’s and no difference between them is known. That is different from the two agreeing, and different again from a difference of zero — this screen showing nothing is the absence of an answer about whether the book ties, not the answer that it does.`,
      ref,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/**
 * Reads the ours-versus-the-institution comparison.
 *
 * One read, no query parameters. `limit` is omitted so the server applies its
 * own cap, which is the largest page it will give (200), and `before` is omitted
 * because this screen does not page — whether it should is a question the
 * truncation note puts in front of an operator rather than one this loader
 * answers by fetching the whole table.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said "I
 * don't know" is a screen the operator can read, not an exception that collapses
 * to a stack trace. A refusal keeps its `decision_ref` (law 13) — the client
 * surfaces it and a screen must not swallow it.
 */
export const loadLedgerBalances: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(LEDGER_BALANCES_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The balances endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    // An AbortError is a navigation away, not a failure to report. Let it
    // propagate so the caller's cancellation stays a cancellation.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isBalancesResponse(payload)) {
    return unreadableScreen('The balances endpoint returned a shape this console does not know.', null)
  }
  return composeLedgerBalances(payload)
}
